/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage, useIntl} from 'react-intl';

import {useProductSearch} from '@salesforce/commerce-sdk-react';
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer';
import {trackSearch, useTrackSelectItem, VIEW_ITEM_LIST} from '@amblique/king-living/app/analytics/ga4';
import {SearchDrawerSubmitIcon, SearchIcon, SubmitSearchIcon} from '@amblique/king-living/app/components/icons';
import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  IconButton,
  Input,
  Text,
  useMediaQuery,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation';
import {productUrlBuilder, searchUrlBuilder} from '@amblique/king-living/app/utils/url';
import {saveRecentSearch} from '@amblique/king-living/app/utils/search-recent';
import {ensureArray} from '@amblique/king-living/app/utils/utils';

import {useContentfulHeader} from '@amblique/king-living/app/hooks/use-contentful-header';
import {useBlogPagesByKeywordSearch} from '@amblique/king-living/app/contentful/hooks';
import SearchIdlePanel from '@amblique/king-living/app/components/search/partials/search-idle-panel';
import SearchRecommendedForYouColumn from '@amblique/king-living/app/components/search/partials/search-recommended-for-you-column';
import SearchTypeaheadPanel from '@amblique/king-living/app/components/search/partials/search-typeahead-panel';
import {mapPageToSpotlightBlog} from '@amblique/king-living/app/components/search/search-spotlight-blog-helpers';
import {
  SEARCH_DRAWER_MAX_ARTICLES_MOBILE,
  SEARCH_DRAWER_MAX_PRODUCTS,
  SEARCH_DRAWER_MAX_PRODUCTS_MOBILE,
  SEARCH_TYPEAHEAD_PRODUCT_HTYPE_REFINE,
  buildTypeaheadRowsFromBlogPages,
  buildTypeaheadRowsFromHits,
  countProductRowsInList,
  mergeAndCapTypeaheadRows,
} from '@amblique/king-living/app/components/search/search-typeahead-helpers';
import {useHeaderContext} from '@amblique/king-living/app/contexts/header-context';
import {SEARCH_TAB_ID} from '@amblique/king-living/app/constants/navigation-drawer';

/** Minimum characters before SCAPI typeahead runs; below this shows Search Idle (Contentful). */
const SEARCH_TYPEAHEAD_MIN_CHARS = 3;

const TYPEAHEAD_EXPAND = Object.freeze([
  'promotions',
  'variations',
  'prices',
  'images',
  'page_meta_tags',
  'custom_properties'
]);

/**
 * Search field for the header modal: Search Idle below 3 chars, SCAPI typeahead at 3+.
 *
 * @param {object} props
 * @param {boolean} [props.inNavDrawer]
 * @param {import('react').RefObject<HTMLInputElement>} [props.inputRef]
 * @param {() => void} [props.onActivate]
 * @param {() => void} [props.onCloseModal]
 */
const Search = ({onCloseModal, inputRef, inNavDrawer = false, onActivate, ...props}) => {
  const intl = useIntl();
  const navigate = useNavigation();
  const {data: customer} = useCurrentCustomer();
  const {site} = useMultiSite();
  const siteId = site?.id;

  const {
    data: contentfulHeader,
    isLoading: isHeaderLoading,
  } = useContentfulHeader(siteId, {
    enabled: Boolean(siteId),
  });

  const searchIdle = contentfulHeader?.searchIdle;
  const searchTyping = contentfulHeader?.searchTyping;

  const [liveInput, setLiveInput] = useState('');
  const [debouncedInput, setDebouncedInput] = useState('');
  const internalRef = useRef(null);
  const searchInputRef = inputRef || internalRef;

  const [isMobile] = useMediaQuery('(max-width: 991px)');
  const {navDrawerTabId} = useHeaderContext();
  const isDrawerSearch = navDrawerTabId === SEARCH_TAB_ID;
  const isNavDrawerMobile = inNavDrawer && isMobile;

  useEffect(() => {
    if (!isDrawerSearch) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [isDrawerSearch, searchInputRef]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedInput(liveInput);
    }, 300);

    return () => window.clearTimeout(handle);
  }, [liveInput]);

  const liveTrimmed = liveInput.trim();
  const debouncedTrimmed = debouncedInput.trim();
  const showIdlePanel = liveTrimmed.length < SEARCH_TYPEAHEAD_MIN_CHARS;
  const trackSelectItem = useTrackSelectItem();

  const productSearchParameters = useMemo(
    () => ({
      q: debouncedTrimmed,
      limit: 12,
      perPricebook: true,
      expand: [...TYPEAHEAD_EXPAND],
      refine: [SEARCH_TYPEAHEAD_PRODUCT_HTYPE_REFINE],
    }),
    [debouncedTrimmed]
  );

  const typeaheadEnabled =
    !showIdlePanel &&
    debouncedTrimmed.length >= SEARCH_TYPEAHEAD_MIN_CHARS;

  const {
    data: productSearchData,
    isLoading: isProductSearchLoading,
    isPreviousData: isProductSearchPreviousData,
  } = useProductSearch(
    {parameters: productSearchParameters},
    {
      enabled: typeaheadEnabled,
      keepPreviousData: true,
    }
  );

  const {
    data: blogSearchCollection,
    isError: isBlogSearchError,
  } = useBlogPagesByKeywordSearch(debouncedTrimmed, {
    limit: isDrawerSearch ? SEARCH_DRAWER_MAX_ARTICLES_MOBILE : 4,
    queryOptions: {
      enabled: typeaheadEnabled && (isDrawerSearch || !isMobile),
    },
  });

  const blogSearchItems = isBlogSearchError ? [] : blogSearchCollection?.items || [];

  const productTagLabel = intl.formatMessage({
    id: 'search.idle.product_tag',
    defaultMessage: 'Product',
  });

  const blogTagLabel = intl.formatMessage({
    id: 'search.typeahead.blog_tag',
    defaultMessage: 'Blog',
  });

  const displayRows = useMemo(() => {
    const hits = productSearchData?.hits || [];
    const productBuilt = buildTypeaheadRowsFromHits(hits, debouncedTrimmed).map((row) => {
      const category = String(row.primaryCategoryName ?? '').trim();
      const tag =
        isDrawerSearch ?
          category :
          row.kind === 'product' && category ?
            `${productTagLabel} / ${category}` :
            productTagLabel;

      return {
        ...row,
        tag,
        onSelect: () => {
          void trackSelectItem({
            index: row.productIndex,
            listMeta: VIEW_ITEM_LIST.QUICK_SEARCH,
            product: row.product,
          });

          if (onCloseModal) {
            onCloseModal();
          }

          navigate(row.link);
        },
      };
    });

    const blogBuilt = isMobile || isDrawerSearch ?
      [] :
      buildTypeaheadRowsFromBlogPages(blogSearchItems, debouncedTrimmed).map(
        (row) => ({
          ...row,
          tag: blogTagLabel,
          onSelect: () => {
            if (onCloseModal) {
              onCloseModal();
            }

            navigate(row.link);
          },
        })
      );

    if (isDrawerSearch) {
      const productCap = isMobile ? SEARCH_DRAWER_MAX_PRODUCTS_MOBILE : SEARCH_DRAWER_MAX_PRODUCTS;

      return productBuilt.slice(0, productCap);
    }

    return mergeAndCapTypeaheadRows(blogBuilt, productBuilt, isMobile);
  }, [
    blogSearchItems,
    blogTagLabel,
    debouncedTrimmed,
    isDrawerSearch,
    isMobile,
    navigate,
    onCloseModal,
    productSearchData?.hits,
    productTagLabel,
    trackSelectItem
  ]);

  const drawerArticles = useMemo(() => {
    if (!isDrawerSearch) {
      return [];
    }

    return (blogSearchItems || [])
      .map(mapPageToSpotlightBlog)
      .filter(Boolean)
      .slice(0, SEARCH_DRAWER_MAX_ARTICLES_MOBILE);
  }, [blogSearchItems, isDrawerSearch, isMobile]);

  const visibleProductRowCount = useMemo(
    () => countProductRowsInList(displayRows),
    [displayRows]
  );

  const viewAllProductTotal = useMemo(() => {
    const total = productSearchData?.total;

    return typeof total === 'number' ? total : undefined;
  }, [productSearchData?.total]);

  const isTypeaheadPending =
    typeaheadEnabled &&
    ((isProductSearchLoading && !productSearchData) || isProductSearchPreviousData);

  const hasDisplayableResults = displayRows.length > 0;
  const showDrawerTypeahead = isDrawerSearch && !showIdlePanel;

  const searchTypingArticleTagCount = useMemo(() => {
    return ensureArray(searchTyping?.articleTags)
      .map((t) => String(t).trim())
      .filter(Boolean).length;
  }, [searchTyping?.articleTags]);

  const showRecommendedForYouDesktop =
    showDrawerTypeahead ||
    (!showIdlePanel &&
      hasDisplayableResults &&
      !isTypeaheadPending &&
      !isMobile &&
      searchTypingArticleTagCount > 0);

  const showNoResultsSuggestions = useMemo(() => {
    return (
      !showIdlePanel &&
      liveTrimmed.length >= SEARCH_TYPEAHEAD_MIN_CHARS &&
      debouncedTrimmed === liveTrimmed &&
      !isTypeaheadPending &&
      !hasDisplayableResults &&
      !isDrawerSearch
    );
  }, [
    debouncedTrimmed,
    hasDisplayableResults,
    isDrawerSearch,
    isTypeaheadPending,
    liveTrimmed.length,
    showIdlePanel,
  ]);

  const hasSettledSearchNoResults = useCallback(
    (query) => {
      const trimmed = String(query || '').trim();

      if (trimmed.length < SEARCH_TYPEAHEAD_MIN_CHARS) {
        return false;
      }

      if (trimmed !== debouncedTrimmed || isTypeaheadPending) {
        return false;
      }

      if (!productSearchData) {
        return false;
      }

      const total = productSearchData.total;

      if (typeof total === 'number') {
        return total === 0;
      }

      return !(productSearchData.hits?.length > 0);
    },
    [debouncedTrimmed, isTypeaheadPending, productSearchData]
  );

  const clearInput = useCallback(() => {
    setLiveInput('');
    setDebouncedInput('');

    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }
  }, [searchInputRef]);

  const onSubmitSearch = useCallback(
    (e) => {
      e.preventDefault();
      const searchText = (searchInputRef.current?.value ?? liveInput).trim();

      if (searchText.length < 1) {
        return;
      }

      saveRecentSearch(searchText);

      if (searchText.length >= SEARCH_TYPEAHEAD_MIN_CHARS) {
        if (searchText !== liveInput) {
          setLiveInput(searchText);
        }

        if (searchText !== debouncedInput) {
          setDebouncedInput(searchText);
          return;
        }

        if (isTypeaheadPending) {
          return;
        }

        if (hasSettledSearchNoResults(searchText)) {
          void trackSearch({customer, searchTerm: searchText});
          return;
        }
      }

      clearInput();

      if (onCloseModal) {
        onCloseModal();
      }

      void trackSearch({customer, searchTerm: searchText});
      navigate(searchUrlBuilder(searchText));
    },
    [
      clearInput,
      customer,
      debouncedInput,
      hasSettledSearchNoResults,
      isTypeaheadPending,
      liveInput,
      navigate,
      onCloseModal,
      saveRecentSearch,
      searchInputRef,
    ]
  );

  const handlePickPopularSearch = useCallback(
    (term) => {
      const t = String(term || '').trim();

      if (!t) {
        return;
      }

      setLiveInput(t);
      setDebouncedInput(t);

      if (searchInputRef.current) {
        searchInputRef.current.value = t;
      }

      searchInputRef.current?.focus();
    },
    [searchInputRef]
  );

  const handlePickBlog = useCallback(
    (blog, {skipNavigate = false} = {}) => {
      const path = String(blog?.pageSlug || '').trim();

      if (!path) {
        return;
      }

      if (onCloseModal) {
        onCloseModal();
      }

      if (!skipNavigate) {
        navigate(path);
      }
    },
    [navigate, onCloseModal]
  );

  const handlePickProduct = useCallback(
    (product, index) => {
      if (typeof index === 'number') {
        void trackSelectItem({
          index,
          listMeta: VIEW_ITEM_LIST.QUICK_SEARCH,
          product,
        });
      }

      const link = productUrlBuilder(product);

      if (!link) {
        return;
      }

      if (onCloseModal) {
        onCloseModal();
      }

      navigate(link);
    },
    [navigate, onCloseModal, trackSelectItem]
  );

  const handleViewAllResults = useCallback(() => {
    const q = debouncedTrimmed;

    if (!q) {
      return;
    }

    saveRecentSearch(q);
    clearInput();

    if (onCloseModal) {
      onCloseModal();
    }

    void trackSearch({customer, searchTerm: q});
    navigate(searchUrlBuilder(q));
  }, [clearInput, customer, debouncedTrimmed, navigate, onCloseModal, saveRecentSearch]);

  const styles = useMultiStyleConfig('SearchForm', {
    variant: inNavDrawer || isDrawerSearch ? 'drawer' : undefined,
  });

  const placeholder = intl.formatMessage({
    id: 'search.field.placeholder',
    defaultMessage: 'Search King',
  });

  const l1Placeholder = intl.formatMessage({
    id: 'mega_menu.search_placeholder',
    defaultMessage: 'Search',
  });

  const submitSearchAriaLabel = intl.formatMessage({
    id: 'search.action.submit.aria_label',
    defaultMessage: 'Submit search',
  });

  const searchFieldAriaLabel = intl.formatMessage({
    id: 'search.field.aria_label',
    defaultMessage: 'Search King',
  });

  const handleInputChange = useCallback((e) => {
    setLiveInput(e.target.value);
  }, []);

  const searchActions = liveTrimmed.length > 0 && !isNavDrawerMobile ? (
    <HStack {...styles.actionsContainer}>
      <Button
        type="button"
        onClick={() => {
          clearInput();
        }}
        {...styles.clearButton}
        color="brandColors.greyscale70"
        variant="unstyled"
      >
        <FormattedMessage
          defaultMessage="Clear"
          id="search.action.clear"
        />
      </Button>
      <IconButton
        aria-label={submitSearchAriaLabel}
        data-testid="search-submit-button"
        icon={
          isDrawerSearch ? (
            <SearchDrawerSubmitIcon {...styles.submitButtonIcon} />
          ) : (
            <SubmitSearchIcon {...styles.submitButtonIcon} />
          )
        }
        sx={styles.submitButton}
        type="submit"
        variant="unstyled"
      />
    </HStack>
  ) : null;

  const searchIcon = isNavDrawerMobile ? (
    <Box
      flexShrink={0}
      h="16px"
      minW="16px"
      w="16px"
      sx={{
        svg: {
          height: '16px',
          maxHeight: '16px',
          maxWidth: '16px',
          width: '16px',
        },
      }}
    >
      <SearchIcon boxSize="16px" />
    </Box>
  ) : (
    <Box {...styles.iconWrapper}>
      <SearchIcon />
    </Box>
  );

  const handleFieldFocus = useCallback(() => {
    if (!isDrawerSearch) {
      onActivate?.();
    }
  }, [isDrawerSearch, onActivate]);

  return (
    <Box
      display={isNavDrawerMobile && isDrawerSearch ? 'flex' : undefined}
      flexDir={isNavDrawerMobile && isDrawerSearch ? 'column' : undefined}
      h={isNavDrawerMobile && isDrawerSearch ? '100%' : undefined}
      minH={isNavDrawerMobile && isDrawerSearch ? 0 : undefined}
      overflow={isNavDrawerMobile && isDrawerSearch ? 'hidden' : undefined}
    >
      <form onSubmit={onSubmitSearch}>
        <Box>
          {isNavDrawerMobile ? (
            <Box flexShrink={0} px="16px" py="8px" w="full">
              <HStack
                align="center"
                bg="whiteAlpha.300"
                borderColor="brandColors.primaryText90"
                borderRadius="4px"
                borderStyle="solid"
                borderWidth={isDrawerSearch ? '2px' : '1px'}
                boxSizing="border-box"
                flexShrink={0}
                gap="12px"
                h="44px"
                maxH="44px"
                overflow="hidden"
                px="16px"
                w="full"
              >
                <Box flex="1" minW={0}>
                  {isDrawerSearch ? (
                    <Text {...styles.floatLabel}>{placeholder}</Text>
                  ) : null}
                  <Input
                    ref={searchInputRef}
                    aria-label={searchFieldAriaLabel}
                    autoComplete="off"
                    id="search-input"
                    placeholder={isDrawerSearch ? '' : l1Placeholder}
                    type="search"
                    value={liveInput}
                    {...styles.input}
                    fontSize="14px"
                    h="auto"
                    lineHeight="1.4"
                    py={0}
                    _placeholder={{
                      color: 'brandColors.greyscale',
                      fontSize: '14px',
                      opacity: 0.5,
                    }}
                    {...props}
                    onChange={handleInputChange}
                    onFocus={handleFieldFocus}
                  />
                </Box>
                {searchIcon}
              </HStack>
            </Box>
          ) : (
            <>
              <Box {...styles.bar}>
                <HStack {...styles.inputContainer}>
                  {searchIcon}
                  <Input
                    ref={searchInputRef}
                    aria-label={searchFieldAriaLabel}
                    autoComplete="off"
                    id="search-input"
                    placeholder={placeholder}
                    type="search"
                    value={liveInput}
                    onChange={handleInputChange}
                    {...styles.input}
                    {...props}
                  />
                  {searchActions}
                </HStack>
                <Divider {...styles.divider} />
              </Box>
            </>
          )}
        </Box>
      </form>

      {(!isNavDrawerMobile || isDrawerSearch) && (
      <Box
        flex={isNavDrawerMobile ? '1 1 auto' : undefined}
        minH={isNavDrawerMobile ? 0 : undefined}
        overflow={isNavDrawerMobile ? 'auto' : undefined}
        px={isDrawerSearch ? {base: '20px', md: '20px', lg: 0} : 0}
        py={isDrawerSearch ? {base: '8px', md: '8px', lg: 0} : 0}
      >
      {showIdlePanel ? (
        <SearchIdlePanel
          isHeaderLoading={isHeaderLoading}
          searchIdle={searchIdle}
          variant={isDrawerSearch ? 'drawer' : undefined}
          onPickBlog={handlePickBlog}
          onPickPopularSearch={handlePickPopularSearch}
          onPickProduct={handlePickProduct}
        />
      ) : showRecommendedForYouDesktop ? (
        <Flex
          align={isDrawerSearch ? 'stretch' : {base: 'stretch', md: 'flex-start'}}
          flexDir={isDrawerSearch ? {base: 'column', md: 'column', lg: 'row'} : {base: 'column', md: 'row'}}
          gap={isDrawerSearch ? {base: 8, md: 8, lg: 20} : {base: 0, md: 10}}
          justify="space-between"
          marginTop={isDrawerSearch ? {base: 0, md: 0, lg: 4} : {base: 0, md: 6}}
          minW={0}
          width="full"
        >
          <Box
            display={isDrawerSearch ? 'flex' : undefined}
            flex={isDrawerSearch ? {base: '1 1 auto', lg: '1 1 auto'} : {base: '1 1 auto', md: 1}}
            flexDir={isDrawerSearch ? 'column' : undefined}
            maxW={isDrawerSearch ? 'full' : {base: 'full', md: '619px'}}
            minW={0}
            w={isDrawerSearch ? {base: 'full', lg: 'auto'} : {base: 'full', md: 'auto'}}
          >
            <SearchTypeaheadPanel
              debouncedQueryTrimmed={debouncedTrimmed}
              displayRows={displayRows}
              hasDisplayableResults={hasDisplayableResults}
              isPanelLoading={isTypeaheadPending}
              liveQueryTrimmed={liveTrimmed}
              marginTop={0}
              minQueryLength={SEARCH_TYPEAHEAD_MIN_CHARS}
              variant={isDrawerSearch ? 'drawer' : undefined}
              viewAllProductTotal={viewAllProductTotal}
              visibleProductRowCount={visibleProductRowCount}
              onViewAllResults={handleViewAllResults}
            />
          </Box>
          <SearchRecommendedForYouColumn
            blogs={isDrawerSearch && drawerArticles.length > 0 ? drawerArticles : undefined}
            isHeaderLoading={isHeaderLoading}
            searchTyping={searchTyping}
            variant={isDrawerSearch ? 'drawer' : undefined}
            onPickBlog={handlePickBlog}
          />
        </Flex>
      ) : (
        <>
          <SearchTypeaheadPanel
            debouncedQueryTrimmed={debouncedTrimmed}
            displayRows={displayRows}
            hasDisplayableResults={hasDisplayableResults}
            isPanelLoading={isTypeaheadPending}
            liveQueryTrimmed={liveTrimmed}
            minQueryLength={SEARCH_TYPEAHEAD_MIN_CHARS}
            variant={isDrawerSearch ? 'drawer' : undefined}
            viewAllProductTotal={viewAllProductTotal}
            visibleProductRowCount={visibleProductRowCount}
            onViewAllResults={handleViewAllResults}
          />
          {showNoResultsSuggestions && (isHeaderLoading || searchIdle) ? (
            <Box
              data-testid="search-overlay-no-results-suggestions"
              marginTop={{base: 6, md: 8}}
              width="100%"
            >
              <SearchIdlePanel
                isHeaderLoading={isHeaderLoading}
                searchIdle={searchIdle}
                variant={isDrawerSearch ? 'drawer' : undefined}
                onPickBlog={handlePickBlog}
                onPickPopularSearch={handlePickPopularSearch}
                onPickProduct={handlePickProduct}
              />
            </Box>
          ) : null}
        </>
      )}
      </Box>
      )}
    </Box>
  );
};

Search.displayName = 'SearchInput';

Search.propTypes = {
  inNavDrawer: PropTypes.bool,
  inputRef: PropTypes.shape({current: PropTypes.any}),
  onActivate: PropTypes.func,
  onCloseModal: PropTypes.func,
};

export default Search;
